// Components/Layout/Pages/EventAdmin/EventAdminOTP.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { postData } from '../../../../services/apiService';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.REACT_APP_CRYPTO_SECRET;

// Encrypt
const encrypt = (data) =>
    CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();

// Decrypt
const decrypt = (cipherText) => {
    try {
        const bytes = CryptoJS.AES.decrypt(cipherText, SECRET_KEY);
        return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch {
        return null;
    }
};

const EventAdminOTP = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [eventName, setEventName] = useState('');
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);
    const [mobile, setMobile] = useState('');
    const [resendTimer, setResendTimer] = useState(0); // Timer in seconds
    const [canResend, setCanResend] = useState(false); // Flag to enable/disable resend button
    const inputRefs = useRef([]);
    const timerRef = useRef(null); // Ref for the timer interval

    // Function to start the countdown timer
    const startResendTimer = () => {
        setCanResend(false);
        setResendTimer(30); // Start from 30 seconds

        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            setResendTimer((prevTimer) => {
                if (prevTimer <= 1) {
                    clearInterval(timerRef.current);
                    setCanResend(true);
                    return 0;
                }
                return prevTimer - 1;
            });
        }, 1000);
    };

    // Function to format timer display (MM:SS)
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        // Get mobile from localStorage
        const mobileData = localStorage.getItem(`event_admin_mobile_${eventId}`);
        if (mobileData) {
            setMobile(mobileData);
        } else {
            // If no mobile in localStorage, redirect back to login
            navigate(`/register/${eventId}/admin`);
        }

        // Focus first input when component is loaded and not loading
        if (inputRefs.current[0] && !loading) {
            setTimeout(() => {
                inputRefs.current[0]?.focus();
            }, 100);
        }

        // Start the timer when component mounts
        startResendTimer();

        // Cleanup timer on component unmount
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [loading, navigate, eventId]);

    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto-focus next input
            if (value && index < 5) {
                setTimeout(() => {
                    inputRefs.current[index + 1]?.focus();
                }, 10);
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (!otp[index] && index > 0) {
                // If current input is empty, focus previous input
                setTimeout(() => {
                    inputRefs.current[index - 1]?.focus();
                }, 10);
            } else if (otp[index]) {
                // If current input has value, clear it
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            setTimeout(() => {
                inputRefs.current[index - 1]?.focus();
            }, 10);
        } else if (e.key === 'ArrowRight' && index < 5) {
            setTimeout(() => {
                inputRefs.current[index + 1]?.focus();
            }, 10);
        } else if (e.key === 'Enter') {
            verifyOTP();
        }
    };
    
    const verifyOTP = async () => {
        const enteredOtp = otp.join('');
        if (enteredOtp.length !== 6 || !/^\d+$/.test(enteredOtp)) {
            Swal.fire({
                title: 'Invalid OTP!',
                text: 'Please enter a valid 6-digit OTP',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            setVerifying(true);

            // Prepare request body for OTP verification
            const requestBody = {
                Event_Id: eventId,
                Mobile_No: mobile,
                Mobile_EmailOtp: enteredOtp
            };

            // Call the API endpoint for OTP verification
            const response = await postData('Event/verify-admin-mob-otp', requestBody);

            if (!response) {
                throw new Error('No response from server');
            }

            if (response.isError === true) {
                throw new Error(response.message || 'OTP verification failed');
            }

            if (response.isError === false) {
                // OTP verified successfully
                Swal.fire({
                    title: 'Success!',
                    text: 'OTP verified successfully!',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    position: 'top-end',
                    toast: true
                });
                const cookieOptions = {
                    secure: process.env.NODE_ENV === 'production',
                    sameSite: 'Strict',
                    path: '/',
                    expires: 1 // 1 day
                };
                const encryptionKey = process.env.REACT_APP_CRYPTO_SECRET || 'your-secret-key';

                const encryptValue = (value) => CryptoJS.AES.encrypt(value.toString(), encryptionKey).toString();

                // Set auth token
                if (response.token) Cookies.set('a_t', encryptValue(response.token), cookieOptions);
                navigate(`/admin/event/${eventId}/dashboard`);
            }
        } catch (err) {
            console.error('Error verifying OTP:', err);

            // Shake animation for wrong OTP
            inputRefs.current.forEach(input => {
                if (input) {
                    input.classList.add('animate-shake');
                    setTimeout(() => {
                        input.classList.remove('animate-shake');
                    }, 500);
                }
            });

            // Clear OTP on error
            setOtp(['', '', '', '', '', '']);
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }

            Swal.fire({
                title: 'Verification Failed!',
                text: err.message || 'Invalid OTP. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setVerifying(false);
        }
    };

    const resendOTP = async () => {
        if (!canResend) return;

        try {
            setResending(true);

            // Prepare request body for resend OTP
            const requestBody = {
                Event_ID: eventId,
                Mobile_No: mobile,
                Password: mobile,
            };

            // Call the API endpoint to resend OTP
            // const response = await postData('Event/resend-admin-otp', requestBody);
            const response = await postData('Event/send-admin-mobile-otp', requestBody);

            if (!response) {
                throw new Error('No response from server');
            }

            if (response.isError === true) {
                throw new Error(response.message || 'Failed to resend OTP');
            }

            if (response.isError === false) {
                // Clear current OTP
                setOtp(['', '', '', '', '', '']);
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }

                // Restart the timer
                startResendTimer();

                Swal.fire({
                    title: 'OTP Resent!',
                    text: 'A new OTP has been sent to your mobile number',
                    icon: 'success',
                    timer: 3000,
                    showConfirmButton: false,
                    position: 'top-end',
                    toast: true
                });
            }
        } catch (err) {
            console.error('Error resending OTP:', err);
            Swal.fire({
                title: 'Error!',
                text: err.message || 'Failed to resend OTP. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setResending(false);
        }
    };

    const goBackToLogin = () => {
        // Optional: Clear the mobile from localStorage if going back
        localStorage.removeItem('event_admin_mobile_47');

        // Clear timer
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        navigate(`/register/${eventId}/admin`);
    };

    if (loading || !mobile) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading verification session...</p>
                </div>
            </div>
        );
    }

    // Format mobile number for display
    const formatMobileNumber = (mobile) => {
        if (mobile && mobile.length === 10) {
            return `+91 ${mobile.substring(0, 5)} ${mobile.substring(5)}`;
        }
        return mobile || '';
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <style jsx>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
                    20%, 40%, 60%, 80% { transform: translateX(2px); }
                }
                .animate-shake {
                    animation: shake 0.5s ease-in-out;
                }
            `}</style>

            <div className="max-w-md w-full space-y-8">
                <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-white/20">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>

                        <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                            Verify OTP
                        </h3>

                        {eventName && (
                            <p className="text-sm text-gray-600 mb-2">
                                For event: <span className="font-semibold">{eventName}</span>
                            </p>
                        )}

                        <p className="text-gray-600 mb-2">
                            Enter the 6-digit OTP sent to
                        </p>

                        <p className="text-lg font-semibold text-gray-900 mb-6">
                            {formatMobileNumber(mobile)}
                        </p>

                        <div className="flex justify-center space-x-3 mb-8">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    id={`otp-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength="1"
                                    className="otp-input w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-200 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    disabled={verifying}
                                />
                            ))}
                        </div>

                        <button
                            onClick={verifyOTP}
                            disabled={verifying || otp.some(digit => digit === '')}
                            className={`w-full py-4 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold transition-all duration-300 transform shadow-lg mb-4 ${!verifying && otp.every(digit => digit !== '')
                                    ? 'hover:from-blue-700 hover:to-purple-700 hover:-translate-y-0.5 hover:shadow-xl'
                                    : ''
                                } ${verifying || otp.some(digit => digit === '') ? 'opacity-50 cursor-not-allowed transform-none' : ''}`}
                        >
                            {verifying ? (
                                <div className="flex items-center justify-center">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                    Verifying...
                                </div>
                            ) : (
                                'Verify & Access Dashboard'
                            )}
                        </button>

                        <div className="flex justify-between items-center">
                            <button
                                type="button"
                                onClick={goBackToLogin}
                                disabled={verifying}
                                className="text-gray-600 hover:text-gray-700 font-medium transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                <span>Back to Login</span>
                            </button>

                            <div className="flex items-center space-x-2">
                                {!canResend ? (
                                    <div className="text-sm text-gray-500 font-medium">
                                        Resend OTP in <span className="font-bold text-blue-600">{formatTimer(resendTimer)}</span>
                                    </div>
                                ) : null}

                                <button
                                    type="button"
                                    onClick={resendOTP}
                                    disabled={!canResend || resending || verifying}
                                    className={`text-blue-600 font-medium transition-colors duration-200 flex items-center space-x-2 ${canResend && !resending && !verifying
                                            ? 'hover:text-blue-700 cursor-pointer'
                                            : 'opacity-50 cursor-not-allowed'
                                        }`}
                                >
                                    {resending ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                            <span>Resending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            <span>Resend OTP</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventAdminOTP;